import { Injectable } from '@angular/core';
import { WlcmPaginatedData } from '@wlcm/common';
import { Location, LocationPayload } from '@core/models/entities/location.models';
import { ApiFeature } from '@core/models/api-feature.models';
import { FormDataObject, FormDataUtils } from '@core/utils/form-data.utils';
import { WlcmQueryParams } from '@wlcm/common';
import { HttpApi } from './http.api';
import { exhaustMap, from, Observable, of, OperatorFunction, switchMap } from 'rxjs';
import { getPlaceDetails } from '@core/utils/place.utils';
import { WlcmPlacesApi } from '@wlcm/angular/forms';

const feature: ApiFeature = new ApiFeature('location', 'locations');

@Injectable({ providedIn: 'root' })
export class LocationApi {
  constructor(
    private readonly httpApi: HttpApi,
    private placeApi: WlcmPlacesApi
  ) {}

  getOne(id: string): Observable<Location> {
    return this.httpApi.get<Location>(`${feature.one}/${id}`);
  }

  getPaginated(params: WlcmQueryParams): Observable<WlcmPaginatedData<Location>> {
    return this.httpApi.get(`${feature.one}`, { params });
  }

  add(payload: LocationPayload): Observable<Location> {
    return of(payload).pipe(
      this.withLocationCoordinates(),
      exhaustMap((updatedPayload: LocationPayload) => this.httpApi.post<Location>(`${feature.one}`, updatedPayload))
    );
  }

  patch(id: string, payload: LocationPayload, addressChanged: boolean = true): Observable<Location> {
    return of(payload).pipe(
      switchMap(() => {
        if (addressChanged) {
          return of(payload).pipe(this.withLocationCoordinates());
        }

        return of(payload);
      }),
      exhaustMap((updatedPayload: LocationPayload) => {
        const formData = FormDataUtils.transform(updatedPayload as FormDataObject<LocationPayload>);

        return this.httpApi.patch<Location>(`${feature.one}/${id}`, formData);
      })
    );
  }

  delete(payload: { id: string }): Observable<boolean> {
    return this.httpApi.delete(`${feature.one}/${payload.id}`);
  }

  withLocationCoordinates(): OperatorFunction<LocationPayload, LocationPayload> {
    return (source$: Observable<LocationPayload>) =>
      source$.pipe(
        switchMap((payload: LocationPayload) => {
          if (!payload?.address?.place_id) return of(payload);

          return from(getPlaceDetails(this.placeApi, payload.address.place_id)).pipe(
            switchMap((details: google.maps.places.PlaceResult | null) => {
              let coords: { lat?: number; lng?: number } = {};

              const latLng: google.maps.LatLng | undefined = details?.geometry?.location;

              const formatted_address: string = details?.formatted_address ?? '';

              if (latLng) {
                coords = { lat: latLng.lat(), lng: latLng.lng() };
              }

              return from([{ ...payload, address: { ...payload.address, ...coords, formatted_address } }]);
            })
          );
        })
      );
  }
}
